import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { PostData } from '../../models/Post';
import { PostLink } from '../../models/ContentPlan';
import { PostChat } from '../PostChat';
import { PostPreview } from './post-chat/PostPreview';
import { ChatPostUpdateData, OriginalValues, PostContext } from '../../types/chat';

interface ContentModalProps {
  isOpen: boolean;
  post: PostData | null;
  onClose: () => void;
  onSave: (updatedPost: PostData) => void;
  contentPlanId: string | undefined;
}

const OverflowStyles = () => (
  <style>{`
    body.overflow-hidden {
      overflow: hidden !important;
      padding-right: var(--scrollbar-compensation);
    }
  `}</style>
);

export const ContentModal: React.FC<ContentModalProps> = ({ isOpen, post, onClose, onSave, contentPlanId }) => {
  const [editedPost, setEditedPost] = useState<PostData | null>(post);
  const [originalPost, setOriginalPost] = useState<PostData | null>(post);
  const [appliedChanges, setAppliedChanges] = useState<Record<string, boolean>>({});
  const [activeTab, setActiveTab] = useState<'preview' | 'chat'>('preview');
  const modalRef = useRef<HTMLDivElement>(null);
  const tempChatPostIdRef = useRef<string | null>(null);

  const hasChanges = useMemo(() => {
    if (!editedPost || !originalPost) return false;
    return (
      editedPost.title !== originalPost.title ||
      editedPost.content !== originalPost.content ||
      editedPost.idea !== originalPost.idea ||
      editedPost.carouselHtmlJson !== originalPost.carouselHtmlJson ||
      editedPost.coverCardHtml !== originalPost.coverCardHtml ||
      editedPost.coverImage !== originalPost.coverImage ||
      editedPost.isReelsShot !== originalPost.isReelsShot ||
      editedPost.postLink !== originalPost.postLink ||
      JSON.stringify(editedPost.links || []) !== JSON.stringify(originalPost.links || [])
    );
  }, [editedPost, originalPost]);

  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.classList.add('overflow-hidden');
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      return () => {
        document.body.classList.remove('overflow-hidden');
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  useEffect(() => {
    if (post) {
      const normalized = {
        ...post,
        platform: post.platform || 'Instagram',
        format: post.format || 'Post',
        title: post.title || '',
        idea: post.idea || '',
        content: post.content || '',
      };
      setEditedPost(normalized);
      setOriginalPost(normalized);
      setAppliedChanges({});

      // Сбрасываем временный id, если прилетел реальный postId
      if (normalized.id && !String(normalized.id).startsWith('temp_')) {
        tempChatPostIdRef.current = null;
      }
    }
  }, [post]);

  useEffect(() => { if (isOpen) setActiveTab('preview'); }, [isOpen]);

  if (!isOpen || !editedPost) return null;

  const effectiveChatPostId = (() => {
    const id = editedPost.id ? String(editedPost.id) : '';
    if (id && !id.startsWith('temp_')) return id;
    if (id && id.startsWith('temp_')) return id;
    if (!tempChatPostIdRef.current) {
      const rnd = Math.random().toString(16).slice(2);
      tempChatPostIdRef.current = `temp_${Date.now()}_${rnd}`;
    }
    return tempChatPostIdRef.current;
  })();

  const handlePostUpdate = (updates: ChatPostUpdateData) => {
    setEditedPost(prev => prev ? {
      ...prev,
      ...(updates.title !== undefined && { title: updates.title }),
      ...(updates.idea !== undefined && { idea: updates.idea }),
      ...(updates.content !== undefined && { content: updates.content }),
      ...(updates.coverImage !== undefined && { coverImage: updates.coverImage }),
      ...(updates.coverCardHtml !== undefined && { coverCardHtml: updates.coverCardHtml }),
      ...(updates.carouselHtmlJson !== undefined && { carouselHtmlJson: updates.carouselHtmlJson })
    } : null);
    
    setAppliedChanges(prev => ({
      ...prev,
      ...(updates.title !== undefined && { title: true }),
      ...(updates.idea !== undefined && { idea: true }),
      ...(updates.content !== undefined && { content: true }),
      ...(updates.carouselHtmlJson !== undefined && { carouselHtmlJson: true }),
      ...(updates.coverCardHtml !== undefined && { coverCardHtml: true }),
    }));
    setActiveTab('preview');
  };

  const handleRevertField = (field: string) => {
    if (!originalPost || !editedPost) return;
    const key = field as keyof OriginalValues;
    const val = (originalPost as any)[key];
    setEditedPost({ ...editedPost, [key]: val });
    setAppliedChanges(prev => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const commonProps = {
    onTitleChange: (val: string) => setEditedPost({ ...editedPost, title: val }),
    onContentChange: (val: string) => setEditedPost({ ...editedPost, content: val }),
    onCarouselHtmlJsonChange: (val: string) => setEditedPost({ ...editedPost, carouselHtmlJson: val }),
    onCoverCardHtmlChange: (val: string) => setEditedPost({ ...editedPost, coverCardHtml: val }),
    onIdeaChange: (val: string) => setEditedPost({ ...editedPost, idea: val }),
    onLinksChange: (val: PostLink[]) => setEditedPost({ ...editedPost, links: val }),
    onReelsShotChange: (val: boolean) => setEditedPost({ ...editedPost, isReelsShot: val }),
    onPostLinkChange: (val: string | undefined) => setEditedPost({ ...editedPost, postLink: val }),
    onImageClick: () => {},
    onCoverDelete: () => setEditedPost({ ...editedPost, coverImage: undefined }),
  };

  const modalContent = (
    <>
      <OverflowStyles />
      <div className="fixed inset-0 bg-black/70 z-[9999] flex justify-center overscroll-none md:items-center md:p-8 backdrop-blur-sm" onClick={onClose}>
        <div ref={modalRef} className="bg-white dark:bg-gray-800 w-full flex flex-col relative overflow-y-auto overscroll-contain md:h-[90vh] h-[100dvh] md:rounded-xl md:shadow-2xl md:max-w-6xl" onClick={e => e.stopPropagation()}>
          <div className="hidden md:flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0 sticky top-0 bg-white dark:bg-gray-800 z-[100]">
            <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">Редактирование поста</h3>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </button>
          </div>

          <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
            <div className="md:hidden flex-1 relative overflow-hidden">
              <div className={`absolute inset-0 py-4 px-3 overflow-y-auto flex justify-center items-stretch ${activeTab === 'preview' ? 'z-10' : 'invisible'}`}>
                <PostPreview post={editedPost} {...commonProps} />
              </div>
              <div className={`absolute inset-0 flex flex-col ${activeTab === 'chat' ? 'z-10' : 'invisible'}`}>
                <PostChat
                  contentPlanId={contentPlanId || ''}
                  postId={effectiveChatPostId}
                  postContext={editedPost as PostContext}
                  onPostUpdate={handlePostUpdate}
                  onRevertField={handleRevertField}
                  originalValues={originalPost as OriginalValues}
                  appliedChanges={appliedChanges}
                  onSwitchToPreview={() => setActiveTab('preview')}
                />
              </div>
            </div>

            <div className="hidden md:flex md:w-1/2 lg:w-[420px] lg:flex-none p-6 overflow-y-auto justify-center items-stretch">
              <PostPreview post={editedPost} {...commonProps} />
            </div>
            
            <div className="hidden md:flex md:w-1/2 lg:flex-1 md:border-l border-gray-200 dark:border-gray-700 flex-col">
              <PostChat
                contentPlanId={contentPlanId || ''}
                postId={effectiveChatPostId}
                postContext={editedPost as PostContext}
                onPostUpdate={handlePostUpdate}
                onRevertField={handleRevertField}
                originalValues={originalPost as OriginalValues}
                appliedChanges={appliedChanges}
                onSwitchToPreview={() => setActiveTab('preview')}
              />
            </div>
          </div>
                      
          <div className="md:hidden flex bg-white dark:bg-gray-800">
            <button onClick={() => setActiveTab('preview')} className={`flex-1 py-3 text-center font-medium ${activeTab === 'preview' ? 'text-blue-600 bg-blue-50' : 'text-gray-500'}`}>Превью</button>
            <button onClick={() => setActiveTab('chat')} className={`flex-1 py-3 text-center font-medium ${activeTab === 'chat' ? 'text-blue-600 bg-blue-50' : 'text-gray-500'}`}>Чат с ИИ</button>
          </div>

          <div className="flex justify-end space-x-3 md:border-t border-gray-200 dark:border-gray-700 p-3 md:p-6 bg-white dark:bg-gray-800 shrink-0">
            <button onClick={onClose} className="px-4 py-1.5 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100">Закрыть</button>
            <button onClick={() => { onSave(editedPost); setOriginalPost(editedPost); setAppliedChanges({}); }} disabled={!hasChanges} className={`px-4 py-1.5 rounded-lg ${hasChanges ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-500 cursor-not-allowed'}`}>Сохранить</button>
          </div>
        </div>
      </div>
    </>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};
